// Pure, framework-free validation for the vendor-details edit form (EX-40771).
// No React, no translate() — validation returns stable error *codes* so it
// stays unit-testable and the consuming component owns localization. Mirrors
// the KYB pattern (src/page-components/OrganizationBankAccount/
// paymentsKyb.validation.ts) at proportional scale.

import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js'

/** Flat string form values, one per editable field. Phone is split into the
 * national-number string plus its ISO country (the add form's own shape), so
 * validation can country-scope the phone check exactly as add mode does. */
export interface VendorFormValues {
  name: string
  email: string
  externalId: string
  contactName: string
  phone: string
  phoneIsoCountry: string
  notes: string
}

export type VendorField =
  | 'name'
  | 'email'
  | 'externalId'
  | 'contactName'
  | 'phone'
  | 'notes'

export type VendorFieldError =
  | 'NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'EMAIL_REQUIRED'
  | 'EMAIL_INVALID'
  | 'EMAIL_TOO_LONG'
  | 'EXTERNAL_ID_TOO_LONG'
  | 'CONTACT_NAME_TOO_LONG'
  | 'PHONE_INVALID'
  | 'NOTES_TOO_LONG'

export type VendorFormErrors = Partial<Record<VendorField, VendorFieldError>>

/**
 * Field length ceilings, from the extend-api wire contract (spec §3). Enforced
 * client-side so an over-long value surfaces under its field instead of coming
 * back as an opaque 422. Phone has no character ceiling here — it is validated
 * by format (E.164-parseable for its country), not length.
 */
export const VENDOR_FIELD_MAX_LENGTHS = {
  name: 255,
  email: 254,
  externalId: 255,
  contactName: 255,
  notes: 1000,
} as const

/** Anchored format check (matches the add form's `isValidEmailFormat`). The
 * shared EMAIL_REGEX is unanchored and matches on substrings. */
const isValidEmailFormat = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/**
 * Validate the whole form and return a code per offending field. Recompute this
 * every render and keep only *visibility* in component state (the KYB approach)
 * — the function itself is pure and holds no state.
 */
export function validateVendorForm(values: VendorFormValues): VendorFormErrors {
  const errors: VendorFormErrors = {}

  const name = values.name.trim()
  if (name === '') {
    errors.name = 'NAME_REQUIRED'
  } else if (name.length > VENDOR_FIELD_MAX_LENGTHS.name) {
    errors.name = 'NAME_TOO_LONG'
  }

  const email = values.email.trim()
  if (email === '') {
    errors.email = 'EMAIL_REQUIRED'
  } else if (!isValidEmailFormat(email)) {
    errors.email = 'EMAIL_INVALID'
  } else if (email.length > VENDOR_FIELD_MAX_LENGTHS.email) {
    errors.email = 'EMAIL_TOO_LONG'
  }

  if (values.externalId.trim().length > VENDOR_FIELD_MAX_LENGTHS.externalId) {
    errors.externalId = 'EXTERNAL_ID_TOO_LONG'
  }

  if (values.contactName.trim().length > VENDOR_FIELD_MAX_LENGTHS.contactName) {
    errors.contactName = 'CONTACT_NAME_TOO_LONG'
  }

  // Phone is optional: only a non-empty, malformed value is an error. Matches
  // the add form (`phoneInvalid`), country-scoped by the picker's ISO.
  const phone = values.phone.trim()
  if (
    phone !== '' &&
    !isValidPhoneNumber(phone, values.phoneIsoCountry as CountryCode)
  ) {
    errors.phone = 'PHONE_INVALID'
  }

  if (values.notes.trim().length > VENDOR_FIELD_MAX_LENGTHS.notes) {
    errors.notes = 'NOTES_TOO_LONG'
  }

  return errors
}

export function isVendorFormValid(errors: VendorFormErrors): boolean {
  return Object.keys(errors).length === 0
}
